import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  midiNoteName,
  selectCurrentProject,
  useSequencerStore,
} from '@/stores/sequencerStore'
import { TICKS_PER_STEP } from '@busswerks/shared'
import { resolvePad } from '@/lib/resolvePad'
import { KITS } from '@/hooks/useAudioEngine'
import { useSequencerTriggers } from '@/hooks/useSequencerTriggers'

/**
 * Piano roll — pitch × time canvas for the currently focused pad
 * and the currently selected pattern.
 *
 * Before this rewrite the view stacked all 16 pads as a tracker-style
 * matrix, which conflated three jobs (per-pad timing, per-note pitch,
 * per-pad routing) in one display. The new layout is a classic piano
 * roll:
 *
 *   Rows  → semitone offsets from the focused pad's natural MIDI
 *           note (±12, a two-octave window). Row 0 = root pitch,
 *           row -12 = octave down, row +11 = major 7th up.
 *   Cols  → steps in the active pattern (`project.stepCount`).
 *
 * Only notes whose `padIndex === focusedPad` render. Cross-pad moves
 * aren't possible from this view — users swap to the tracker for
 * drum-machine layouts, or focus a different pad to edit its notes
 * in isolation.
 *
 * ## Gestures
 *
 * | Gesture | Effect |
 * |---|---|
 * | Click empty grid cell | Add a note at click position + snapped tick |
 * | Drag note body | Move (time ±, pitch ±) |
 * | Drag note right edge | Resize length (snapped) |
 * | Click note body without moving | Remove |
 * | Alt + scroll | Velocity ±0.05 |
 * | Shift + scroll | Pitch offset ±1 semitone |
 *
 * ## Snap-to-grid
 *
 * Step snapping is on by default — drags round to the nearest whole
 * step (`TICKS_PER_STEP`). Toggle to tick-precision when you need
 * sub-step accents. State is local to the component; persisting
 * the preference is a follow-up.
 *
 * ## Drag/drop bug fix
 *
 * The previous version's row `onClick` added a note on drag release
 * because the browser fires a synthetic click on the mouseup target's
 * closest parent if its descendants didn't block it. The note's
 * `onMouseDown` called `e.stopPropagation()` but that doesn't apply
 * to the later click event. Fix: the grid's onClick bails unless
 * the click was on the grid itself (`e.target === e.currentTarget`).
 */
interface PianoRollProps {
  fullScreen?: boolean
}

/** Total pitch-offset rows rendered. Two-octave window centred on 0,
 *  ordered highest-first (visually like a keyboard laid on its side
 *  with high notes at the top). */
const PITCH_ROWS: ReadonlyArray<number> = Array.from(
  { length: 24 },
  (_, i) => 11 - i,
)

export function PianoRoll({ fullScreen = false }: PianoRollProps) {
  const project = useSequencerStore(selectCurrentProject)
  const focusedPad = useSequencerStore(s => s.focusedPad)
  const addPatternNote = useSequencerStore(s => s.addPatternNote)
  const removePatternNote = useSequencerStore(s => s.removePatternNote)
  const updatePatternNote = useSequencerStore(s => s.updatePatternNote)
  const clearPatternNotes = useSequencerStore(s => s.clearPatternNotes)
  const currentStep = useSequencerStore(s => s.currentStep)
  const isPlaying = useSequencerStore(s => s.isPlaying)

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const gridRef = useRef<HTMLDivElement | null>(null)

  const notes = project?.notes ?? []
  const stepCount = project?.stepCount ?? 16
  const totalTicks = stepCount * TICKS_PER_STEP

  const [snapToStep, setSnapToStep] = useState(true)

  // Pitch-row audition. Pressing+holding a row label attacks the
  // focused pad at that pitch offset; releasing (or pointer-leave /
  // pointer-cancel) releases the voice. Tokens are tracked per-row so
  // a pointer that slides across rows cleans up each row's voice
  // individually. No-ops on non-instrument pads — `attackPad`
  // short-circuits them to a one-shot firePad.
  const { attackPad, releasePad, resume } = useSequencerTriggers()
  const auditionTokensRef = useRef(new Map<number, number | null>())
  const auditionAttack = useCallback(
    (offset: number) => {
      resume()
      // If a token is already live for this row (double pointerdown
      // without an intervening leave), release it first so we don't
      // leak.
      const existing = auditionTokensRef.current.get(offset)
      if (existing !== undefined) releasePad(existing)
      const token = attackPad(focusedPad, { pitch: offset })
      auditionTokensRef.current.set(offset, token)
    },
    [attackPad, releasePad, resume, focusedPad],
  )
  const auditionRelease = useCallback(
    (offset: number) => {
      const token = auditionTokensRef.current.get(offset)
      if (token === undefined) return
      releasePad(token)
      auditionTokensRef.current.delete(offset)
    },
    [releasePad],
  )
  // Release every in-flight audition on unmount — prevents stuck
  // notes when the user navigates away mid-press.
  useEffect(() => {
    const tokens = auditionTokensRef.current
    return () => {
      for (const token of tokens.values()) {
        if (token !== null) releasePad(token)
      }
      tokens.clear()
    }
  }, [releasePad])

  /** Quantise a tick value under the current snap setting. Step
   *  snapping rounds to the nearest whole step; tick mode leaves the
   *  value alone. */
  const snapTick = (tick: number) =>
    snapToStep ? Math.round(tick / TICKS_PER_STEP) * TICKS_PER_STEP : tick

  // Focused-pad label (sidebar title) — the sound that will fire for
  // every note in this view. Kept as a display-only string; the fire
  // path reads `project.padDefs[focusedPad]` directly.
  const focusedLabel = useMemo(() => {
    if (!project) return 'empty'
    const kit = KITS[project.kitId]
    const voiceList: Array<(typeof kit.voices)[number] | undefined> = new Array(
      kit.voices.length,
    ).fill(undefined)
    kit.voices.forEach((v, i) => {
      voiceList[i] = v
    })
    return resolvePad(project, voiceList, focusedPad)?.label ?? 'empty'
  }, [project, focusedPad])

  // Filter down to this pad's notes. Unknown pad → empty.
  const padNotes = useMemo(
    () => notes.filter(n => n.padIndex === focusedPad),
    [notes, focusedPad],
  )

  // Keep the play cursor on-screen while playback rolls. Mirrors the
  // prior component's gentle auto-scroll so long patterns don't hide
  // the cursor off the right edge.
  useEffect(() => {
    if (!isPlaying || currentStep < 0) return
    const el = scrollRef.current
    if (!el) return
    const labelColPx = 64
    const stepAreaWidth = Math.max(1, el.scrollWidth - labelColPx)
    const cursorX = labelColPx + (currentStep / stepCount) * stepAreaWidth
    const visibleLeft = el.scrollLeft
    const visibleRight = el.scrollLeft + el.clientWidth
    const margin = el.clientWidth * 0.2
    if (cursorX < visibleLeft + margin || cursorX > visibleRight - margin) {
      el.scrollTo({
        left: Math.max(0, cursorX - el.clientWidth * 0.2),
        behavior: 'smooth',
      })
    }
  }, [currentStep, isPlaying, stepCount])

  // Click the grid to add a note. The `e.target === e.currentTarget`
  // guard is the critical drag-release fix — without it, releasing a
  // drag on an existing note bubbles a synthetic click into this
  // handler and duplicates the note at the drop point.
  const handleGridClick = (e: React.MouseEvent) => {
    if (!project) return
    if (e.target !== e.currentTarget) return
    const grid = gridRef.current
    if (!grid) return
    const rect = grid.getBoundingClientRect()
    const relX = e.clientX - rect.left
    const relY = e.clientY - rect.top
    const rowH = rect.height / PITCH_ROWS.length
    const rowIdx = Math.max(
      0,
      Math.min(PITCH_ROWS.length - 1, Math.floor(relY / rowH)),
    )
    const pitchOffset = PITCH_ROWS[rowIdx]!
    const rawTick = Math.round((relX / rect.width) * totalTicks)
    const tickStart = Math.max(0, Math.min(totalTicks - 1, snapTick(rawTick)))
    const tickLength = snapToStep ? TICKS_PER_STEP : TICKS_PER_STEP
    addPatternNote({
      padIndex: focusedPad,
      pitchOffset,
      tickStart,
      tickLength,
      velocity: 0.85,
    })
  }

  // In-pane: fill available vertical space via flex-1 (the tab
  // container stretches to its parent grid's height). Full-screen:
  // pin to a near-viewport height as before.
  const heightClass = fullScreen
    ? 'h-[calc(100dvh-11rem)]'
    : 'flex-1 min-h-[18rem]'
  const baseMidiNote = useMemo(() => {
    if (!project) return 60
    const kit = KITS[project.kitId]
    const voiceList: Array<(typeof kit.voices)[number] | undefined> = new Array(
      kit.voices.length,
    ).fill(undefined)
    kit.voices.forEach((v, i) => {
      voiceList[i] = v
    })
    return resolvePad(project, voiceList, focusedPad)?.midiNote ?? 60
  }, [project, focusedPad])

  return (
    <div className="flex flex-col gap-1.5 flex-1 min-h-0">
      <div className="flex items-center gap-3 text-[10px] font-mono text-content-tertiary">
        <span className="uppercase tracking-wider text-content-secondary">
          Pad {focusedPad + 1}
        </span>
        <span className="truncate" title={focusedLabel}>
          {focusedLabel}
        </span>
        <span className="flex-1" />
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={snapToStep}
            onChange={e => setSnapToStep(e.target.checked)}
            className="accent-[color:var(--accent)]"
          />
          <span className="uppercase tracking-wider">Snap to step</span>
        </label>
        {padNotes.length > 0 && (
          <>
            <span>
              {padNotes.length} note{padNotes.length === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              onClick={() => {
                if (
                  window.confirm(
                    `Remove all ${padNotes.length} note${padNotes.length === 1 ? '' : 's'} on pad ${focusedPad + 1}?`,
                  )
                ) {
                  // The store's clearPatternNotes clears the whole
                  // pattern; we scope this button's action to the
                  // focused pad by removing each note individually
                  // so other pads' notes stay intact.
                  for (const n of padNotes) removePatternNote(n.id)
                }
              }}
              className="hover:text-destructive underline underline-offset-2 uppercase tracking-wider"
              title="Remove notes on the focused pad only"
            >
              Clear pad
            </button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Remove all notes from this pattern?')) {
                  clearPatternNotes()
                }
              }}
              className="hover:text-destructive underline underline-offset-2 uppercase tracking-wider"
              title="Remove every note from the active pattern"
            >
              Clear all
            </button>
          </>
        )}
      </div>

      <div
        ref={scrollRef}
        className={`${heightClass} overflow-auto rounded border border-brd-subtle bg-surface-sunken`}
        aria-label={`Piano roll for pad ${focusedPad + 1}`}
      >
        <div className="min-w-[800px] relative">
          {/* Play cursor. Sits inside the step-area column (offset
              by the note-letter sidebar) so step 0 aligns with the
              leftmost cell. Hidden when stopped (currentStep < 0). */}
          {isPlaying && currentStep >= 0 && (
            <div
              aria-hidden="true"
              className="absolute top-0 bottom-0 pointer-events-none z-20"
              style={{ left: '4rem', right: 0 }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: `${(currentStep / stepCount) * 100}%`,
                  width: '1px',
                  background: 'var(--accent)',
                  opacity: 0.8,
                }}
              />
            </div>
          )}

          {/* Step ruler (sticky top). */}
          <div className="flex border-b border-brd-subtle bg-surface-base sticky top-0 z-10 text-[9px] font-mono text-content-tertiary">
            <div className="w-16 flex-shrink-0 border-r border-brd-subtle px-1 py-0.5">
              Note
            </div>
            <div className="flex-1 flex">
              {Array.from({ length: stepCount }).map((_, i) => (
                <div
                  key={i}
                  className="flex-1 border-r border-brd-subtle/40 px-1 py-0.5"
                >
                  {i + 1}
                </div>
              ))}
            </div>
          </div>

          {/* Pitch rows. The whole stack shares one click target
              (`<div ref={gridRef}>`) so grid math + snap logic lives
              in one place; row labels render separately in a fixed-
              width column that doesn't intercept clicks. */}
          <div className="flex">
            <div className="w-16 flex-shrink-0 border-r border-brd-subtle bg-surface-raised/40">
              {PITCH_ROWS.map(offset => {
                const midi = baseMidiNote + offset
                const isSharp = midiNoteName(midi).includes('#')
                return (
                  <div
                    key={offset}
                    role="button"
                    tabIndex={-1}
                    onPointerDown={e => {
                      e.preventDefault()
                      // Capture so pointerup / cancel still fire on
                      // this element even if the pointer leaves the
                      // row during the press.
                      e.currentTarget.setPointerCapture(e.pointerId)
                      auditionAttack(offset)
                    }}
                    onPointerUp={e => {
                      try {
                        e.currentTarget.releasePointerCapture(e.pointerId)
                      } catch {
                        /* already released */
                      }
                      auditionRelease(offset)
                    }}
                    onPointerCancel={() => auditionRelease(offset)}
                    onPointerLeave={() => auditionRelease(offset)}
                    className={`h-6 border-b border-brd-subtle/40 px-1 py-0.5 text-[10px] font-mono flex items-center gap-1 cursor-pointer select-none transition-colors hover:bg-surface-base/60 active:bg-[color:var(--accent)]/30 ${
                      isSharp ? 'bg-surface-base/40' : ''
                    }`}
                    title={`${offset > 0 ? '+' : ''}${offset} semitones (${midiNoteName(midi)}) — hold to audition`}
                  >
                    <span className="text-content-tertiary tabular-nums w-6">
                      {offset > 0 ? '+' : ''}
                      {offset}
                    </span>
                    <span className="text-content-secondary">
                      {midiNoteName(midi)}
                    </span>
                  </div>
                )
              })}
            </div>
            <div
              ref={gridRef}
              role="button"
              tabIndex={0}
              aria-label="Piano-roll grid — click to add a note"
              onClick={handleGridClick}
              className="flex-1 relative cursor-crosshair"
              style={{ minHeight: `${PITCH_ROWS.length * 1.5}rem` }}
            >
              {/* Horizontal row guides. Pointer-events-none so the
                  click-to-add path through the grid still fires. */}
              <div className="absolute inset-0 pointer-events-none">
                {PITCH_ROWS.map((offset, idx) => {
                  const midi = baseMidiNote + offset
                  const isSharp = midiNoteName(midi).includes('#')
                  return (
                    <div
                      key={offset}
                      className={`absolute left-0 right-0 h-6 border-b border-brd-subtle/40 ${
                        isSharp
                          ? 'bg-surface-base/30'
                          : idx === 12 // row 0 = root — subtle accent
                            ? 'bg-accent/5'
                            : ''
                      }`}
                      style={{ top: `${idx * 1.5}rem` }}
                    />
                  )
                })}
              </div>

              {/* Vertical step guides — one border per step. */}
              <div className="absolute inset-0 pointer-events-none flex">
                {Array.from({ length: stepCount }).map((_, i) => (
                  <div
                    key={i}
                    className="flex-1 border-r border-brd-subtle/25"
                  />
                ))}
              </div>

              {padNotes.map(note => {
                // Find the row index whose offset matches the note's
                // pitchOffset. Notes outside the ±12 window get
                // clamped to the edges so they stay visible + can be
                // dragged back into range.
                let rowIdx = PITCH_ROWS.findIndex(o => o === note.pitchOffset)
                if (rowIdx < 0) {
                  rowIdx = note.pitchOffset > 11 ? 0 : PITCH_ROWS.length - 1
                }
                const top = rowIdx * 1.5
                const left = (note.tickStart / totalTicks) * 100
                const width = (note.tickLength / totalTicks) * 100
                const hue = ((note.pitchOffset + 12) * 15) % 360

                const handleNoteMouseDown = (
                  e: React.MouseEvent<HTMLDivElement>,
                ) => {
                  if (e.button !== 0) return
                  e.stopPropagation()
                  e.preventDefault()
                  const grid = gridRef.current
                  if (!grid) return
                  const rect = grid.getBoundingClientRect()
                  const rowH = rect.height / PITCH_ROWS.length
                  const startX = e.clientX
                  const startY = e.clientY
                  const startTick = note.tickStart
                  const startOffset = note.pitchOffset
                  let moved = false
                  const onMove = (mv: MouseEvent) => {
                    const dx = mv.clientX - startX
                    const dy = mv.clientY - startY
                    if (!moved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
                      moved = true
                    }
                    if (!moved) return
                    const tickDelta = Math.round((dx / rect.width) * totalTicks)
                    const rowDelta = Math.round(dy / rowH)
                    const rawNextTick = Math.max(
                      0,
                      Math.min(
                        totalTicks - note.tickLength,
                        startTick + tickDelta,
                      ),
                    )
                    const nextTick = snapTick(rawNextTick)
                    // Vertical direction is inverted: dragging UP =
                    // lower row index = higher pitch offset.
                    const rawNextOffset = startOffset - rowDelta
                    const nextOffset = Math.max(
                      PITCH_ROWS[PITCH_ROWS.length - 1]!,
                      Math.min(PITCH_ROWS[0]!, rawNextOffset),
                    )
                    const patch: Partial<{
                      tickStart: number
                      pitchOffset: number
                    }> = {}
                    if (nextTick !== note.tickStart) patch.tickStart = nextTick
                    if (nextOffset !== note.pitchOffset)
                      patch.pitchOffset = nextOffset
                    if (Object.keys(patch).length > 0) {
                      updatePatternNote(note.id, patch)
                    }
                  }
                  const onUp = () => {
                    window.removeEventListener('mousemove', onMove)
                    window.removeEventListener('mouseup', onUp)
                    // Click-without-move on an existing note removes
                    // it. The onClick→handleGridClick path is guarded
                    // by `e.target === e.currentTarget` so it never
                    // re-adds a note at the drop location.
                    if (!moved) removePatternNote(note.id)
                  }
                  window.addEventListener('mousemove', onMove)
                  window.addEventListener('mouseup', onUp)
                }

                const handleResizeStart = (
                  e: React.MouseEvent<HTMLDivElement>,
                ) => {
                  e.stopPropagation()
                  e.preventDefault()
                  const grid = gridRef.current
                  if (!grid) return
                  const rect = grid.getBoundingClientRect()
                  const startTick = note.tickStart
                  const startLength = note.tickLength
                  const onMove = (mv: MouseEvent) => {
                    const relX = Math.max(
                      0,
                      Math.min(rect.width, mv.clientX - rect.left),
                    )
                    const rawEnd = Math.round((relX / rect.width) * totalTicks)
                    const endTick = Math.max(
                      startTick + 1,
                      Math.min(totalTicks, snapTick(rawEnd)),
                    )
                    const nextLength = Math.max(1, endTick - startTick)
                    if (nextLength !== startLength) {
                      updatePatternNote(note.id, { tickLength: nextLength })
                    }
                  }
                  const onUp = () => {
                    window.removeEventListener('mousemove', onMove)
                    window.removeEventListener('mouseup', onUp)
                  }
                  window.addEventListener('mousemove', onMove)
                  window.addEventListener('mouseup', onUp)
                }

                const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
                  if (!e.altKey && !e.shiftKey) return
                  e.preventDefault()
                  e.stopPropagation()
                  const direction = e.deltaY < 0 ? 1 : -1
                  if (e.altKey) {
                    const next = Math.max(
                      0.05,
                      Math.min(1, note.velocity + direction * 0.05),
                    )
                    updatePatternNote(note.id, { velocity: next })
                  } else if (e.shiftKey) {
                    const next = Math.max(
                      -24,
                      Math.min(24, note.pitchOffset + direction),
                    )
                    updatePatternNote(note.id, { pitchOffset: next })
                  }
                }

                return (
                  <div
                    key={note.id}
                    className="absolute rounded cursor-grab"
                    style={{
                      top: `${top + 0.1}rem`,
                      height: '1.3rem',
                      left: `${left}%`,
                      width: `${Math.max(0.5, width)}%`,
                      background: `hsla(${hue}, 60%, 50%, 0.8)`,
                      opacity: Math.max(0.35, note.velocity),
                    }}
                    title={`${note.pitchOffset > 0 ? '+' : ''}${note.pitchOffset} semi · vel ${note.velocity.toFixed(2)} · drag to move · click to remove · drag right edge to resize · alt+scroll vel · shift+scroll pitch`}
                    onMouseDown={handleNoteMouseDown}
                    onWheel={handleWheel}
                  >
                    <div
                      role="separator"
                      aria-label="Resize note"
                      onMouseDown={handleResizeStart}
                      onClick={e => e.stopPropagation()}
                      className="absolute top-0 bottom-0 right-0 w-1 bg-[color:var(--accent-ink)]/30 hover:bg-[color:var(--accent-ink)]/80 cursor-ew-resize rounded-r"
                    />
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
